<script setup lang="ts">
import type { FieldSize } from '@/components/fields/KTextField.vue';

import { ref, useTemplateRef, watch } from 'vue';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { format } from 'date-fns-tz';

import { checkValidDate } from '@/utils/date';
import FallbackMdiIconCmn from '@/components/cmns/FallbackMdiIconCmn.vue';
import KTextField from '@/components/fields/KTextField.vue';
import usePickerField from '@/composables/usePickerField';

import datepickerStyles from '@/styles/datepicker.module.scss';

export type WeekPickerFieldValue = [Date, Date];

export interface Props {
  modelValue?: WeekPickerFieldValue | null;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  hideDetails?: boolean;
  rules?: object;
  size?: FieldSize;
  autoApply?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  label: 'Week',
  loading: false,
  disabled: false,
  hideDetails: false,
  rules: () => {
    return [];
  },
  size: undefined,
  autoApply: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: WeekPickerFieldValue | null];
}>();

const fieldFormatPartPattern = 'dd MMM yyyy';

const fieldValue = ref<WeekPickerFieldValue | null>();
const datepickerEl = useTemplateRef('datepicker');
const root = ref<HTMLElement | null>(null);

function getFieldValue(value: unknown): WeekPickerFieldValue | null {
  if (
    Array.isArray(value) &&
    checkValidDate(value[0]) &&
    checkValidDate(value[1])
  ) {
    return [value[0], value[1]];
  } else {
    return null;
  }
}

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue === undefined) {
      fieldValue.value = undefined;
    } else {
      fieldValue.value = getFieldValue(newModelValue);
    }
  },
  { immediate: true },
);

function handleUpdateModelValue(value: unknown): void {
  fieldValue.value = getFieldValue(value);
  emit('update:modelValue', fieldValue.value);
}

function formatWeekPicker(): string {
  const value = fieldValue.value;
  if (!Array.isArray(value)) return '';
  const fromDate = value[0];
  const toDate = value[1];
  return (
    format(fromDate, fieldFormatPartPattern) +
    '–' +
    format(toDate, fieldFormatPartPattern)
  );
}

const {
  pickerAttrs,
  handleAllEnterKeydown,
  // handleFocus,
  // handleClick,
  handleAllEscKeydown,
  handleAppendIconClick,
  handleAllSpaceKeydown,
  handleDownKeydown,
} = usePickerField(root, datepickerEl);
</script>

<template>
  <div ref="root" data-testid="weekPickerField">
    <VueDatePicker
      v-bind="pickerAttrs"
      ref="datepicker"
      :model-value="fieldValue"
      :formats="{ input: formatWeekPicker }"
      :auto-apply="props.autoApply"
      :class="[
        $style.weekPickerField,
        {
          [$style.datepickerHideDetails]: props.hideDetails,
        },
      ]"
      :ui="{
        menu: datepickerStyles.dpCustomMenu,
        calendar: datepickerStyles.dpCustomCalendar,
      }"
      :time-config="{ enableTimePicker: false }"
      :text-input="{
        enterSubmit: true,
        tabSubmit: true,
        openMenu: !props.disabled ? 'open' : false,
      }"
      week-picker
      @update:model-value="handleUpdateModelValue"
      @keydown.enter="handleAllEnterKeydown"
      @keydown.space="handleAllSpaceKeydown"
      @keydown.esc="handleAllEscKeydown"
    >
      <template #arrow-left>
        <FallbackMdiIconCmn icon="mdi-chevron-left" />
      </template>
      <template #arrow-right>
        <FallbackMdiIconCmn icon="mdi-chevron-right" />
      </template>
      <template #arrow-up>
        <FallbackMdiIconCmn icon="mdi-chevron-up" />
      </template>
      <template #arrow-down>
        <FallbackMdiIconCmn icon="mdi-chevron-down" />
      </template>
      <template #calendar-icon>
        <FallbackMdiIconCmn icon="mdi-calendar-blank-outline" />
      </template>
      <template
        #dp-input="{
          value,
          onInput,
          onEnter,
          onTab,
          onClear,
          onBlur,
          onKeypress,
          onPaste,
          isMenuOpen,
        }"
      >
        <KTextField
          append-inner-icon="mdi-calendar-blank-outline"
          :label="props.label"
          clearable
          :model-value="value"
          :loading="props.loading"
          :disabled="props.disabled"
          :hide-details="props.hideDetails"
          :rules="props.rules"
          :size="props.size"
          :class="$style.field"
          readonly
          @input="onInput"
          @keydown.tab="onTab"
          @blur="onBlur"
          @keydown.down.prevent="handleDownKeydown($event, isMenuOpen)"
          @paste="onPaste"
          @click:clear="onClear"
          @click:append-inner="handleAppendIconClick($event, isMenuOpen)"
        />
      </template>
    </VueDatePicker>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.weekPickerField {
  padding-bottom: 22px;
}
.datepickerHideDetails {
  padding-bottom: 0;
}

.field {
  :global {
    .v-field {
      cursor: pointer;
    }
    input.v-field__input {
      padding-right: 0;
      cursor: pointer;
    }
    .v-input__details {
      position: absolute;
      bottom: -22px;
    }
  }
}
</style>
